import React, { useState, useEffect, useRef, useCallback } from 'react';

// Represents a generic IC block in the SVG
const IcChip = ({ x, y, width, height, label, color, inputs = [], outputs = [], onMouseOver, onMouseOut }) => {
  const pinSpacing = 15;
  const pinLength = 15;
  const totalInputHeight = inputs.length * pinSpacing;
  const totalOutputHeight = outputs.length * pinSpacing;
  const inputStartY = (height - totalInputHeight + pinSpacing) / 2;
  const outputStartY = (height - totalOutputHeight + pinSpacing) / 2;

  return (
    <g transform={`translate(${x}, ${y})`} onMouseOver={onMouseOver} onMouseOut={onMouseOut} style={{ cursor: 'pointer' }}>
      <rect x="0" y="0" width={width} height={height} fill={color} stroke="#333" strokeWidth="1.5" rx="5" ry="5" />
      <text x={width / 2} y="-10" textAnchor="middle" fontSize="14" fontWeight="bold">{label}</text>
      {/* Input Pins */}
      {inputs.map((pin, i) => (
        <g key={`in-${pin.label}-${i}`}>
          <line x1={-pinLength} y1={inputStartY + i * pinSpacing} x2="0" y2={inputStartY + i * pinSpacing} stroke="#333" strokeWidth="1.5" />
          <text x={-pinLength - 5} y={inputStartY + i * pinSpacing + 4} textAnchor="end" fontSize="10">{pin.label}</text>
          {/* Connection point */}
          <circle cx={-pinLength} cy={inputStartY + i * pinSpacing} r="2" fill="#333" data-pin-id={`${label}-in-${pin.label}`} />
        </g>
      ))}
      {/* Output Pins */}
      {outputs.map((pin, i) => (
        <g key={`out-${pin.label}-${i}`}>
          <line x1={width} y1={outputStartY + i * pinSpacing} x2={width + pinLength} y2={outputStartY + i * pinSpacing} stroke="#333" strokeWidth="1.5" />
          <text x={width + pinLength + 5} y={outputStartY + i * pinSpacing + 4} textAnchor="start" fontSize="10">{pin.label}</text>
           {/* Connection point */}
          <circle cx={width + pinLength} cy={outputStartY + i * pinSpacing} r="2" fill="#333" data-pin-id={`${label}-out-${pin.label}`} />
        </g>
      ))}
    </g>
  );
};

// Represents the DIP switches
const DipSwitch = ({ x, y, bits, binaryRepresentation, onMouseOver, onMouseOut }) => {
  const switchWidth = 8;
  const switchHeight = 30;
  const switchSpacing = 12;
  const totalWidth = bits * switchSpacing + 10; // Add some padding
  const height = 50;

  return (
     <g
       transform={`translate(${x}, ${y})`}
       onMouseOver={onMouseOver}
       onMouseOut={onMouseOut}
       style={{ cursor: 'pointer' }}
     >
      <rect x="0" y="0" width={totalWidth} height={height} fill="#e0e0e0" stroke="#333" strokeWidth="1.5" rx="5" ry="5"/>
      <text x={totalWidth / 2} y="-10" textAnchor="middle" fontSize="14" fontWeight="bold">Hex Input</text>
      {Array.from({ length: bits }).map((_, i) => {
        const bitIndex = bits - 1 - i; // Display MSB (bit 3) on the left
        const isOn = binaryRepresentation[bitIndex] === '1';
        const switchX = 5 + i * switchSpacing;
        return (
          <g key={`switch-${i}`} transform={`translate(${switchX}, 10)`}>
            <rect x="0" y="0" width={switchWidth} height={switchHeight} fill="#d0d0d0" stroke="#555" strokeWidth="0.5" />
            <rect
              x="1"
              y={isOn ? "3" : "15"} // Position of the slider
              width={switchWidth - 2}
              height={switchHeight / 2 - 2}
              fill={isOn ? "#66bb6a" : "#aaa"} // Green for ON, Gray for OFF
              stroke="#333"
              strokeWidth="0.5"
            />
            <text x={switchWidth / 2} y={switchHeight + 10} textAnchor="middle" fontSize="9">B{bitIndex}</text>
             {/* Output Connection point */}
             <circle cx={switchWidth / 2} cy={height} r="2" fill="#333" data-pin-id={`DIP-out-B${bitIndex}`} />
             <line x1={switchWidth / 2} y1={height - 10} x2={switchWidth / 2} y2={height} stroke="#333" strokeWidth="1.5" />
          </g>
        );
      })}
    </g>
  );
}

// Represents the LED Matrix display
const LedMatrixDisplay = ({ x, y, size, activeLeds, onMouseOver, onMouseOut }) => {
  const matrixSize = 160; // SVG size
  const ledSize = 6;
  const padding = 15;
  const spacing = (matrixSize - 2 * padding) / (size - 1);

  return (
    <g transform={`translate(${x}, ${y})`} onMouseOver={onMouseOver} onMouseOut={onMouseOut} style={{ cursor: 'pointer' }}>
      <rect x="0" y="0" width={matrixSize} height={matrixSize} fill="#2d2d2d" stroke="#333" strokeWidth="2" rx="5" ry="5"/>
      <text x={matrixSize / 2} y="-10" textAnchor="middle" fontSize="14" fontWeight="bold">8x8 Matrix</text>
      {/* LEDs */}
      {activeLeds.map((row, rIndex) =>
        row.map((isLit, cIndex) => (
          <circle
            key={`matrix-led-${rIndex}-${cIndex}`}
            cx={padding + cIndex * spacing}
            cy={padding + rIndex * spacing}
            r={ledSize / 2}
            fill={isLit ? "#ff5555" : "#444"}
            stroke={isLit ? "#ff8888" : "#333"}
            strokeWidth="0.5"
            filter={isLit ? "url(#glow)" : "none"}
          />
        ))
      )}
      {/* Row connection points (assuming common cathode, driven LOW) */}
      {Array.from({ length: size }).map((_, i) => (
         <g key={`row-conn-${i}`}>
            <line x1={-15} y1={padding + i * spacing} x2="0" y2={padding + i * spacing} stroke="#333" strokeWidth="1.5" />
            <circle cx={-15} cy={padding + i * spacing} r="2" fill="#333" data-pin-id={`MATRIX-in-R${i}`} />
            <text x={-20} y={padding + i * spacing + 4} textAnchor="end" fontSize="9">R{i}</text>
         </g>
      ))}
       {/* Column connection points (assuming common cathode, driven HIGH) */}
       {Array.from({ length: size }).map((_, i) => (
         <g key={`col-conn-${i}`}>
            <line x1={padding + i * spacing} y1={matrixSize} x2={padding + i * spacing} y2={matrixSize + 15} stroke="#333" strokeWidth="1.5" />
            <circle cx={padding + i * spacing} cy={matrixSize + 15} r="2" fill="#333" data-pin-id={`MATRIX-in-C${i}`} />
             <text x={padding + i * spacing} y={matrixSize + 25} textAnchor="middle" fontSize="9">C{i}</text>
         </g>
      ))}
      {/* LED glow filter */}
      <defs>
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>
    </g>
  );
};

// Wire drawing component
const Wire = ({ startPinId, endPinId, svgRef, isActive = false }) => {
  const [pathData, setPathData] = useState("M 0 0");

  useEffect(() => {
    if (!svgRef.current) return;

    // Use requestAnimationFrame to ensure positions are calculated after layout updates
    requestAnimationFrame(() => {
        const svgRect = svgRef.current.getBoundingClientRect();
        const startElement = svgRef.current.querySelector(`[data-pin-id="${startPinId}"]`);
        const endElement = svgRef.current.querySelector(`[data-pin-id="${endPinId}"]`);

        if (startElement && endElement) {
          const startRect = startElement.getBoundingClientRect();
          const endRect = endElement.getBoundingClientRect();

          // Calculate coordinates relative to the SVG container's top-left corner
          const startX = startRect.left + startRect.width / 2 - svgRect.left;
          const startY = startRect.top + startRect.height / 2 - svgRect.top;
          const endX = endRect.left + endRect.width / 2 - svgRect.left;
          const endY = endRect.top + endRect.height / 2 - svgRect.top;

          // Simple orthogonal path routing (horizontal-vertical-horizontal or vertical-horizontal-vertical)
          const midX = startX + (endX - startX) / 2;
          const midY = startY + (endY - startY) / 2;

          // Prefer H-V-H for primarily horizontal connections
          if (Math.abs(startX - endX) > Math.abs(startY - endY)) {
             setPathData(`M ${startX} ${startY} H ${midX} V ${endY} H ${endX}`);
          } else { // Prefer V-H-V for primarily vertical connections
             setPathData(`M ${startX} ${startY} V ${midY} H ${endX} V ${endY}`);
          }

        } else {
            // console.warn(`Could not find elements for wire: ${startPinId} to ${endPinId}`);
            setPathData("M 0 0"); // Hide wire if pins aren't found
        }
    });
  }, [startPinId, endPinId, svgRef]); // Rerun if pins or SVG ref changes

  return (
    <path
      d={pathData}
      stroke={isActive ? "#ff5555" : "#4a90e2"} // Blue wires, red when active
      strokeWidth={isActive ? "2.5" : "1.5"}
      fill="none"
      style={{ transition: 'stroke 0.2s ease, stroke-width 0.2s ease' }}
    />
  );
};


const CircuitSimulation = () => {
  const [hexInput, setHexInput] = useState('A'); // Default to 'A'
  const [activeComponent, setActiveComponent] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState({ title: '', description: '' });
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [binaryRepresentation, setBinaryRepresentation] = useState('0000');
  const [activeLeds, setActiveLeds] = useState(Array(8).fill().map(() => Array(8).fill(false)));
  const [currentRow, setCurrentRow] = useState(0); // State for the currently scanned row (0-7)
  const [currentColumnData, setCurrentColumnData] = useState(0); // State for the 8-bit data from ROM/Latch

  const svgContainerRef = useRef(null); // Ref for SVG container
  const mainContainerRef = useRef(null); // Ref for the main component container

  // --- Simulation State ---
  const clockInterval = 150; // ms, determines refresh rate (adjust for desired speed)

  // Component descriptions (Updated)
  const componentInfo = {
    dip: {
      title: "Hexadecimal Input (DIP Switches)",
      description: "4 DIP switches provide the 4-bit binary input (B3, B2, B1, B0) representing the hex character (0-F)."
    },
    '555': {
      title: "555 Timer (Clock)",
      description: `Generates clock pulses (~${(1000/clockInterval).toFixed(1)} Hz) driving the row counter. Frequency determines refresh rate.`
    },
    '74HC161': {
      title: "74HC161 (Counter)",
      description: "A 4-bit synchronous binary counter. We use 3 bits (Q0, Q1, Q2) to count from 0 to 7, selecting the matrix row."
    },
    '74HC138': {
      title: "74HC138 (Decoder)",
      description: "A 3-to-8 line decoder. Takes the 3-bit row address from the counter and activates (LOW) one of the 8 output lines (Y0-Y7) to select a row."
    },
    'ROM': {
      title: "ROM (e.g., 27C64 EPROM)",
      description: "Read-Only Memory storing the 8x8 patterns. Address = 4 bits (Hex Input) + 3 bits (Row Address). Outputs 8 bits of column data for the selected row."
    },
     '74HC573': {
       title: "74HC573 (Latch)",
       description: "An 8-bit D-type latch. Briefly stores the 8-bit column data from the ROM while the row is being activated, ensuring stable column output."
     },
    'ULN2803': {
      title: "Row Driver (ULN2803)",
      description: "Darlington array IC used as a sinking driver. When an input (from inverted Decoder output) is HIGH, it connects the corresponding matrix row cathode (-) to ground, activating the row."
    },
    'UDN2981': {
      title: "Column Driver (UDN2981)",
      description: "Source driver IC. When an input (from Latch output) is HIGH, it connects the corresponding matrix column anode (+) to +5V (through a resistor), lighting up LEDs in the active row."
    },
    'RESISTORS': {
        title: "Column Resistors",
        description: "Set of 8 current-limiting resistors (e.g., 220Ω). One per column, placed between the column driver output and the matrix anode to protect LEDs."
    },
    ledMatrix: {
      title: "8x8 LED Matrix (Common Cathode)",
      description: "Grid of 64 LEDs. Rows (cathodes) are selected by grounding them (via ULN2803). Columns (anodes) are driven HIGH (via UDN2981) to light LEDs."
    }
  };

  // --- ROM Data Structure ---
  const fontPatterns = {
      '0': [0x3C, 0x42, 0x42, 0x42, 0x42, 0x42, 0x3C, 0x00], // 00111100, 01000010, ...
      '1': [0x10, 0x30, 0x50, 0x10, 0x10, 0x10, 0x7C, 0x00], // 00010000, 00110000, ...
      '2': [0x3C, 0x42, 0x02, 0x0C, 0x30, 0x40, 0x7E, 0x00],
      '3': [0x3C, 0x42, 0x02, 0x3C, 0x02, 0x42, 0x3C, 0x00],
      '4': [0x08, 0x18, 0x28, 0x48, 0xFE, 0x08, 0x08, 0x00],
      '5': [0x7E, 0x40, 0x40, 0x7C, 0x02, 0x02, 0x7C, 0x00],
      '6': [0x3C, 0x40, 0x40, 0x7C, 0x42, 0x42, 0x3C, 0x00],
      '7': [0x7E, 0x02, 0x04, 0x08, 0x10, 0x10, 0x10, 0x00],
      '8': [0x3C, 0x42, 0x42, 0x3C, 0x42, 0x42, 0x3C, 0x00],
      '9': [0x3C, 0x42, 0x42, 0x3E, 0x02, 0x02, 0x3C, 0x00],
      'A': [0x3C, 0x42, 0x42, 0x7E, 0x42, 0x42, 0x42, 0x00],
      'B': [0x7C, 0x42, 0x42, 0x7C, 0x42, 0x42, 0x7C, 0x00], // Similar to 8 but with a closed loop
      'C': [0x3C, 0x40, 0x40, 0x40, 0x40, 0x40, 0x3C, 0x00],
      'D': [0x78, 0x44, 0x42, 0x42, 0x42, 0x44, 0x78, 0x00],
      'E': [0x7E, 0x40, 0x40, 0x7C, 0x40, 0x40, 0x7E, 0x00],
      'F': [0x7E, 0x40, 0x40, 0x7C, 0x40, 0x40, 0x40, 0x00],
      'DEFAULT': [0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00],
  };

  // Convert Hex input to 4-bit binary string
  const getBinaryFromHex = useCallback((hex) => {
     if (!hex || !/^[0-9A-Fa-f]$/.test(hex[0])) {
        return '0000';
     }
     return parseInt(hex[0], 16).toString(2).padStart(4, '0');
  }, []);


  // --- Simulation Logic ---
  useEffect(() => {
    // Update binary representation when hex input changes
    setBinaryRepresentation(getBinaryFromHex(hexInput));

    // Update the full LED matrix preview based on the input character
    const pattern = fontPatterns[hexInput?.toUpperCase()[0] || 'DEFAULT'] || fontPatterns['DEFAULT'];
    const newMatrix = pattern.map(rowData =>
        rowData.toString(2).padStart(8, '0').split('').map(bit => bit === '1')
    );
    setActiveLeds(newMatrix);

  }, [hexInput, getBinaryFromHex]);

  // Clock, Counter, and Multiplexing Simulation Effect
   useEffect(() => {
     const timerId = setInterval(() => {
       // 1. Counter increments (0-7 cycle)
       setCurrentRow(prevRow => (prevRow + 1) % 8);

       // 2. ROM Lookup (Conceptual)
       const hexValue = parseInt(getBinaryFromHex(hexInput), 2); // 0-15
       const romAddress = (hexValue << 3) | currentRow; // Combine hex and row for 7-bit address
       const fullPattern = fontPatterns[hexInput?.toUpperCase()[0] || 'DEFAULT'] || fontPatterns['DEFAULT'];
       const rowData = fullPattern[currentRow] || 0; // Get the 8-bit number for this row
       setCurrentColumnData(rowData);

     }, clockInterval);

     return () => clearInterval(timerId); // Cleanup interval on unmount
   }, [hexInput, currentRow, getBinaryFromHex]); // Rerun if hexInput changes


  // --- Event Handlers ---
  const handleHexInputChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^0-9A-F]/g, '').substring(0, 1);
    setHexInput(value);
  };

  // Updated handleMouseOver for better tooltip positioning
  const handleMouseOver = (componentKey, e) => {
    const info = componentInfo[componentKey];
    if (!info) return;

    const rect = e.currentTarget.getBoundingClientRect(); // Element position relative to viewport

    setActiveComponent(componentKey);
    setTooltipContent({ title: info.title, description: info.description });
    // Position tooltip based on viewport coordinates, adding scroll offset
    setTooltipPosition({
      x: rect.left + window.scrollX + rect.width / 2, // Center horizontally relative to element
      y: rect.top + window.scrollY - 10 // Position above the element
    });
    setShowTooltip(true);
  };

  const handleMouseOut = () => {
    setActiveComponent(null);
    setShowTooltip(false);
  };

  // --- Pin Definitions for Wiring ---
  const counterOutputs = ['Q0', 'Q1', 'Q2'];
  const decoderInputs = ['A0', 'A1', 'A2'];
  const decoderOutputs = Array.from({ length: 8 }, (_, i) => `Y${i}`);
  const romAddressInputs = Array.from({ length: 7 }, (_, i) => `A${i}`); // A0-A2 from counter, A3-A6 from DIP
  const romDataOutputs = Array.from({ length: 8 }, (_, i) => `D${i}`);
  const latchInputs = Array.from({ length: 8 }, (_, i) => `D${i}`);
  const latchOutputs = Array.from({ length: 8 }, (_, i) => `Q${i}`);
  const rowDriverInputs = Array.from({ length: 8 }, (_, i) => `IN${i}`);
  const rowDriverOutputs = Array.from({ length: 8 }, (_, i) => `OUT${i}`);
  const colDriverInputs = Array.from({ length: 8 }, (_, i) => `IN${i}`);
  const colDriverOutputs = Array.from({ length: 8 }, (_, i) => `OUT${i}`);
  const dipOutputs = ['B0', 'B1', 'B2', 'B3'];


  return (
    // Added ref to the main container
    <div ref={mainContainerRef} className="relative flex flex-col items-center p-2 bg-gray-100 rounded-lg overflow-hidden font-sans">
      <h2 className="text-xl font-bold mb-4 text-gray-800">8x8 LED Matrix Simulation (No Microcontroller)</h2>

      {/* Control Panel */}
      <div className="mb-4 p-3 bg-white rounded-lg shadow-md w-full flex items-center justify-around space-x-4 z-10"> {/* Added z-10 */}
        {/* Hex Input */}
        <div className="flex flex-col items-center">
          <label className="block text-sm font-medium mb-1 text-gray-700">Hex Input (0-F):</label>
          <input
            type="text"
            value={hexInput}
            onChange={handleHexInputChange}
            maxLength="1"
            className="border p-2 rounded shadow-sm w-16 text-center text-lg font-mono"
          />
        </div>

        {/* Binary Display */}
        <div className="text-center">
           <div className="text-sm font-medium mb-1 text-gray-700">Binary (B3 B2 B1 B0):</div>
           <div className="border p-2 rounded bg-gray-100 text-lg font-mono shadow-inner">{binaryRepresentation}</div>
        </div>

        {/* Simulation Info */}
         <div className="text-center">
           <div className="text-sm font-medium mb-1 text-gray-700">Active Row:</div>
           <div className="border p-2 rounded bg-gray-100 text-lg font-mono shadow-inner w-12">{currentRow}</div>
         </div>
         <div className="text-center">
           <div className="text-sm font-medium mb-1 text-gray-700">Column Data (Bin):</div>
           <div className="border p-2 rounded bg-gray-100 text-sm font-mono shadow-inner">{currentColumnData.toString(2).padStart(8,'0')}</div>
         </div>

        {/* LED Preview */}
        <div className="flex flex-col items-center">
           <div className="text-sm font-medium mb-1 text-gray-700">LED Preview:</div>
           <div className="border p-1 bg-black rounded shadow-inner inline-block">
             <div className="grid grid-cols-8 gap-px">
               {activeLeds.map((row, rowIndex) => (
                 row.map((isLit, colIndex) => (
                   <div
                     key={`led-preview-${rowIndex}-${colIndex}`}
                     className="w-3 h-3 rounded-full"
                     style={{
                       backgroundColor: isLit ? '#ff5555' : '#333',
                       boxShadow: isLit ? '0 0 4px #ff0000' : 'none',
                       transition: 'background-color 0.1s ease', // Smooth transition for preview
                     }}
                   />
                 ))
               ))}
             </div>
           </div>
         </div>
      </div>

      {/* Circuit Diagram Area */}
      {/* Removed relative positioning here, tooltip is now relative to the main div */}
      <div className="w-full bg-gradient-to-br from-blue-50 via-white to-gray-100 p-4 rounded-lg shadow-inner overflow-auto border border-gray-300" style={{ height: '650px' }}>
        <svg ref={svgContainerRef} width="1400" height="900" viewBox="0 0 1400 900">
          {/* Background Grid */}
          <defs>
             <pattern id="smallGrid" width="10" height="10" patternUnits="userSpaceOnUse">
               <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#e0e0e0" strokeWidth="0.5"/>
             </pattern>
             <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse">
               <rect width="100" height="100" fill="url(#smallGrid)"/>
               <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#cccccc" strokeWidth="1"/>
             </pattern>
           </defs>
           <rect width="1400" height="900" fill="url(#grid)" />

          {/* --- Components --- */}
          {/* Components definitions remain the same... */}
           <DipSwitch x={50} y={50} bits={4} binaryRepresentation={binaryRepresentation}
              onMouseOver={(e) => handleMouseOver('dip', e)} onMouseOut={handleMouseOut} />

          <IcChip x={50} y={200} width={80} height={100} label="555" color="#ffcc80"
              outputs={[{label: 'CLK'}]}
              onMouseOver={(e) => handleMouseOver('555', e)} onMouseOut={handleMouseOut} />

          <IcChip x={50} y={400} width={90} height={120} label="74HC161" color="#e1bee7"
              inputs={[{label: 'CLK'}]} outputs={counterOutputs.map(l => ({ label: l }))}
              onMouseOver={(e) => handleMouseOver('74HC161', e)} onMouseOut={handleMouseOut} />

          <IcChip x={250} y={400} width={90} height={160} label="74HC138" color="#fff59d"
              inputs={decoderInputs.map(l => ({ label: l }))} outputs={decoderOutputs.map(l => ({ label: l }))}
              onMouseOver={(e) => handleMouseOver('74HC138', e)} onMouseOut={handleMouseOut} />

           {/* ROM */}
           <IcChip x={450} y={150} width={100} height={200} label="ROM" color="#b3e5fc"
              inputs={romAddressInputs.map(l => ({ label: l }))} outputs={romDataOutputs.map(l => ({ label: l }))}
              onMouseOver={(e) => handleMouseOver('ROM', e)} onMouseOut={handleMouseOut} />

           {/* Latch */}
           <IcChip x={650} y={150} width={90} height={200} label="74HC573" color="#f8bbd0"
              inputs={latchInputs.map(l => ({ label: l })).concat({label: 'LE'})} outputs={latchOutputs.map(l => ({ label: l }))}
              onMouseOver={(e) => handleMouseOver('74HC573', e)} onMouseOut={handleMouseOut} />

            {/* Row Drivers */}
            <IcChip x={450} y={500} width={90} height={200} label="ULN2803" color="#a5d6a7"
               inputs={rowDriverInputs.map(l => ({ label: l }))} outputs={rowDriverOutputs.map(l => ({ label: l }))}
               onMouseOver={(e) => handleMouseOver('ULN2803', e)} onMouseOut={handleMouseOut} />

             {/* Column Drivers */}
             <IcChip x={900} y={150} width={90} height={200} label="UDN2981" color="#ffab91"
               inputs={colDriverInputs.map(l => ({ label: l }))} outputs={colDriverOutputs.map(l => ({ label: l }))}
               onMouseOver={(e) => handleMouseOver('UDN2981', e)} onMouseOut={handleMouseOut} />

             {/* Column Resistors */}
             <g transform="translate(1050, 150)" onMouseOver={(e) => handleMouseOver('RESISTORS', e)} onMouseOut={handleMouseOut}>
                 <rect x="0" y="0" width="60" height={200} fill="#f0f0f0" stroke="#777" strokeWidth="1" rx="5"/>
                 <text x="30" y="-10" textAnchor="middle" fontSize="14" fontWeight="bold">Resistors</text>
                 {Array.from({length: 8}).map((_, i) => (
                     <g key={`res-io-${i}`}>
                         <circle cx="-5" cy={20 + i * 22} r="2" fill="#333" data-pin-id={`RES-in-${i}`} />
                         <line x1="-5" y1={20 + i * 22} x2="0" y2={20 + i * 22} stroke="#555" strokeWidth="1"/>
                         <rect x="10" y={12 + i * 22} width="40" height="16" fill="none" stroke="#888" strokeWidth="1.5"/>
                         <line x1="50" y1={20 + i * 22} x2="65" y2={20 + i * 22} stroke="#555" strokeWidth="1"/>
                         <circle cx="65" cy={20 + i * 22} r="2" fill="#333" data-pin-id={`RES-out-${i}`} />
                     </g>
                 ))}
             </g>

            {/* LED Matrix */}
            <LedMatrixDisplay x={1150} y={150} size={8} activeLeds={activeLeds}
                onMouseOver={(e) => handleMouseOver('ledMatrix', e)} onMouseOut={handleMouseOut} />


          {/* --- Wires --- */}
          {/* Wires definitions remain the same... */}
          {/* Clock */}
          <Wire startPinId="555-out-CLK" endPinId="74HC161-in-CLK" svgRef={svgContainerRef} isActive={true} />
          {/* Counter to Decoder */}
          {counterOutputs.map((pin, i) => (<Wire key={`wire-count-dec-${i}`} startPinId={`74HC161-out-${pin}`} endPinId={`74HC138-in-${decoderInputs[i]}`} svgRef={svgContainerRef} isActive={true} />))}
          {/* Decoder to Row Driver */}
          {decoderOutputs.map((pin, i) => (<Wire key={`wire-dec-rowdrv-${i}`} startPinId={`74HC138-out-${pin}`} endPinId={`ULN2803-in-IN${i}`} svgRef={svgContainerRef} isActive={i === currentRow} /> ))}
          {/* Row Driver to Matrix Rows */}
          {rowDriverOutputs.map((pin, i) => ( <Wire key={`wire-rowdrv-matrix-${i}`} startPinId={`ULN2803-out-${pin}`} endPinId={`MATRIX-in-R${i}`} svgRef={svgContainerRef} isActive={i === currentRow} /> ))}
          {/* DIP Switches to ROM Address */}
          {dipOutputs.map((pin, i) => ( <Wire key={`wire-dip-rom-${i}`} startPinId={`DIP-out-${pin}`} endPinId={`ROM-in-A${i + 3}`} svgRef={svgContainerRef} isActive={binaryRepresentation[i] === '1'} /> ))}
          {/* Counter to ROM Address */}
          {counterOutputs.map((pin, i) => ( <Wire key={`wire-count-rom-${i}`} startPinId={`74HC161-out-${pin}`} endPinId={`ROM-in-A${i}`} svgRef={svgContainerRef} isActive={true} /> ))}
          {/* ROM Data to Latch Input */}
          {romDataOutputs.map((pin, i) => ( <Wire key={`wire-rom-latch-${i}`} startPinId={`ROM-out-${pin}`} endPinId={`74HC573-in-D${i}`} svgRef={svgContainerRef} isActive={(currentColumnData >> i) & 1} /> ))}
          {/* Latch Output to Column Driver Input */}
          {latchOutputs.map((pin, i) => ( <Wire key={`wire-latch-coldrv-${i}`} startPinId={`74HC573-out-${pin}`} endPinId={`UDN2981-in-IN${i}`} svgRef={svgContainerRef} isActive={(currentColumnData >> i) & 1} /> ))}
          {/* Column Driver Output to Resistors */}
          {colDriverOutputs.map((pin, i) => ( <Wire key={`wire-coldrv-res-${i}`} startPinId={`UDN2981-out-${pin}`} endPinId={`RES-in-${i}`} svgRef={svgContainerRef} isActive={(currentColumnData >> i) & 1} /> ))}
          {/* Resistors to Matrix Columns */}
          {Array.from({length: 8}).map((_, i) => ( <Wire key={`wire-res-matrix-${i}`} startPinId={`RES-out-${i}`} endPinId={`MATRIX-in-C${i}`} svgRef={svgContainerRef} isActive={(currentColumnData >> i) & 1} /> ))}

        </svg>
      </div>

      {/* Tooltip - Moved outside the SVG container div */}
      {showTooltip && (
        <div
          className="absolute bg-gray-800 text-white p-3 rounded-lg shadow-xl text-xs z-50 max-w-xs pointer-events-none" // Increased shadow and z-index
          style={{
            left: `${tooltipPosition.x}px`, // Position based on viewport + scroll
            top: `${tooltipPosition.y}px`,  // Position based on viewport + scroll
            transform: 'translate(-50%, -100%)', // Adjust to position above the cursor/component
            transition: 'opacity 0.2s ease', // Optional: fade effect
            opacity: 1,
          }}
        >
          <h4 className="font-bold mb-1">{tooltipContent.title}</h4>
          <p>{tooltipContent.description}</p>
        </div>
      )}

    </div>
  );
};

export default CircuitSimulation; // Make sure to export App or the main component